import _ from 'lodash';
import { parse, stringify } from 'query-string';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { apiUpdateClientOfferApproach } from '../../../../../actions/ClientRfpActions';
import { sleep } from '../../../../../helpers/common';
import { arraySortFuncMultiKeys } from '../../../../../helpers/common/ArrayHelper';
/**
 * 検索用のクエリの取得
 * @returns {{sortItems: [{label: string, value: string}, {label: string, value: string}], isView: string, queryParam: unknown, sort: string, viewItems: [{label: string, value: string}, {label: string, value: string}], queryRef: React.MutableRefObject<{}>}}
 */
const useContentQuery = () => {
    const { search } = useLocation();
    const queryParam = useMemo(() => parse(search), [search]);
    const viewItems = useMemo(() => [
        { label: 'OFF', value: 'off' },
        { label: 'ON', value: 'on' },
    ], []);
    const sortItems = useMemo(() => [
        { label: '提案魅力度順', value: 'charm_point' },
        { label: '提案金額順', value: 'price' },
    ], []);
    const queryRef = useRef({});
    const { isView, sort } = useMemo(() => {
        const { is_view: queryIsView, sort: querySort } = queryParam || {};
        const isViewValues = viewItems.map((v) => v.value);
        const sortItemValues = sortItems.map((v) => v.value);
        queryRef.current.isView = isViewValues.includes(queryIsView)
            ? queryIsView
            : 'off';
        queryRef.current.sort = sortItemValues.includes(querySort)
            ? querySort
            : 'charm_point';
        return {
            isView: queryRef.current.isView,
            sort: queryRef.current.sort,
        };
    }, [queryParam, viewItems, sortItems, queryRef]);
    return {
        isView,
        queryParam,
        queryRef,
        sort,
        sortItems,
        viewItems,
    };
};
const useContent = (id, rawOffers, dispatch, unmountRef) => {
    const history = useNavigate();
    const { isView, queryParam, queryRef, sort, sortItems, viewItems } = useContentQuery();
    /**
     * offerの並びを再計算した結果を返す関数
     * @type {function(*=, *, *): T[]}
     */
    const calculateSortOffers = useCallback((calcOffers, isViewState, sortState) => {
        // 提案金額が設定されていない場合は、最大提案金額を適用する
        const priceSortFunc = (value) => value.price || value.max_price;
        const firstSort = sortState === 'charm_point'
            ? { adjust: -1, name: 'charm_point' }
            : { name: priceSortFunc };
        return (calcOffers || [])
            .filter((v) => {
            if (isViewState === 'on') {
                return !v.offer.is_view;
            }
            return true;
        })
            .sort((leftValue, rightValue) => arraySortFuncMultiKeys(leftValue.offer, rightValue.offer, [
            firstSort,
            'charm_point_seq',
        ]));
    }, []);
    const rawOfferRef = useRef([]);
    rawOfferRef.current = rawOffers;
    const defaultOffers = useMemo(() => calculateSortOffers(rawOffers, isView, sort), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, calculateSortOffers]);
    const [selects, changeSelects] = useState([]);
    const [offers, changeOffers] = useState(defaultOffers || []);
    /**
     * 安全なsleep後の処理実行
     * @type {function(*=, *): void}
     */
    const safeSleep = useCallback((waitSeconds, callback) => {
        sleep(waitSeconds).then(() => {
            if (unmountRef.current) {
                return;
            }
            callback();
        });
    }, [unmountRef]);
    /**
     * 指定の検索条件でoffersの再ソート＋state更新
     * @type {function(*=, *=, *=): void}
     */
    const updateNewOffers = useCallback(() => {
        const calcResult = calculateSortOffers(rawOfferRef.current, queryRef.current.isView, queryRef.current.sort);
        const newOffers = calcResult
            .map((v) => v.id)
            .map((sortId) => rawOfferRef.current.find((v) => v.id === sortId))
            .filter((v) => v);
        changeOffers(newOffers);
    }, [calculateSortOffers, changeOffers, queryRef, rawOfferRef]);
    /**
     * 検索条件変更やデータ引き直しによるofferの再計算
     */
    useEffect(() => {
        updateNewOffers();
    }, [updateNewOffers, rawOffers, isView, sort]);
    /**
     * 提案魅力度更新による並び替え＋アニメーションの実行
     * @type {function(): void}
     */
    const changeApproachOrder = useCallback(() => {
        const parent = document.querySelector('.interview_offer_box');
        const { top: parentTop } = parent.getBoundingClientRect();
        // 新しい並び順の取得
        let calcTop = 0;
        const newIndexes = calculateSortOffers(rawOfferRef.current, queryRef.current.isView, queryRef.current.sort);
        const result = newIndexes.map((v) => {
            const findClassName = `.offer_row_${v.id}`;
            const rect = document.querySelector(findClassName).getBoundingClientRect();
            const topPosition = parentTop + calcTop;
            calcTop += rect.height + 15;
            const y = topPosition - rect.top;
            return [v.id, y ? { transform: `translateY(${y}px)` } : {}];
        });
        const newStyles = _.fromPairs(result);
        changeOffers((prev) => prev.map((v) => (Object.assign(Object.assign({}, v), { style: newStyles[v.id] }))));
        safeSleep(0.5, () => {
            // order再計算
            updateNewOffers();
        });
    }, [calculateSortOffers, updateNewOffers, safeSleep, rawOfferRef, queryRef]);
    /**
     * 提案の一覧でファイルがあるかどうか
     * @type {boolean}
     */
    const hasFile = useMemo(() => offers.some((v) => v.offer.file || v.offer.additional_files.length > 0), [offers]);
    /**
     * 未読有無の検索条件の変更
     * @type {function(*): void}
     */
    const onChangeIsView = useCallback((e) => {
        const { value } = e.currentTarget;
        const newParam = Object.assign(Object.assign({}, queryParam), { is_view: value });
        const newQuery = stringify(newParam);
        changeSelects([]);
        history.push(`/project/client/${id}/interview/view?${newQuery}`, {
            preventScrollReset: true,
            state: undefined,
        });
    }, [id, history, queryParam, changeSelects]);
    /**
     * ソート条件の変更
     * @type {function(*): void}
     */
    const onChangeSort = useCallback((selectValue) => {
        const { value } = selectValue;
        const newParam = Object.assign(Object.assign({}, queryParam), { sort: value });
        const newQuery = stringify(newParam);
        changeSelects([]);
        history.push(`/project/client/${id}/interview/view?${newQuery}`, {
            preventScrollReset: true,
            state: undefined,
        });
    }, [id, history, queryParam, changeSelects]);
    /**
     * チェックの状態の変更
     * @type {function(*=): void}
     */
    const onChangeRowSelect = useCallback((value) => {
        changeSelects((prev) => {
            const hasValue = !!prev.find((v) => v.id === value.id);
            if (hasValue) {
                return prev.filter((v) => v.id !== value.id);
            }
            return [...prev, value];
        });
    }, [changeSelects]);
    /**
     * 提案魅力度更新後のイベント
     * @type {function(*=, *): void}
     */
    const onChangeRowApproach = useCallback((viewRequestId, charmPoint) => {
        dispatch(apiUpdateClientOfferApproach(id, viewRequestId, { charmPoint }));
        if (sort === 'charm_point') {
            safeSleep(0.5, changeApproachOrder);
        }
    }, [id, dispatch, changeApproachOrder, safeSleep, sort]);
    return {
        changeSelects,
        hasFile,
        isView,
        offers,
        onChangeIsView,
        onChangeRowApproach,
        onChangeRowSelect,
        onChangeSort,
        selects,
        sort,
        sortItems,
        viewItems,
    };
};
export const rfpInterviewContentUseCase = {
    useContent,
};
export default rfpInterviewContentUseCase;
